import { ExportButton } from "@/components/ExportButton";
import {
  Button,
  CreateButton,
  DatagridConfigurable as Datagrid,
  EditButton,
  FilterButton,
  FunctionField,
  List,
  NumberField,
  Pagination,
  SelectColumnsButton,
  SimpleList,
  TopToolbar,
  WrapperField,
  usePermissions,
  useStore,
  type Exporter,
  type RaRecord,
} from "react-admin";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useMediaQuery } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { buildPassMembers, downloadPasses, formatINR, isAsharaEvent } from "@/utils";
import { formatVenueLabel } from "@/utils/event-venue-utils";
import { buildNiyaazListFilters } from "./niyaazListFilters";
import { hasPermission } from "@/utils/permission-utils";
import { exportToExcel } from "@/utils/export-to-excel";
import type { CurrentEvent } from "@/containers/events/types";
import type { PassesProps } from "@/components/pdf";
import DisplayDateField from "@/components/DisplayDateField";
import PhoneLink from "@/components/PhoneLink";

const countNiyaazMembers = (record: RaRecord, includeMehmaans: boolean) => {
  const family = Array.isArray(record.familyMembers) ? record.familyMembers.length : 0;
  if (!includeMehmaans) return family;
  const mehmaans = Array.isArray(record.mehmaans) ? record.mehmaans.length : 0;
  return family + mehmaans;
};

const HOF_COLUMN_CLASS = "niyaaz-list-hof-column";
const FORM_VENUE_COLUMN_CLASS = "niyaaz-list-form-venue-column";

const renderNiyaazFormVenueCell = (
  record: RaRecord,
  currentEvent: CurrentEvent | null,
  isAshara: boolean
) => {
  const jamanLabel = formatVenueLabel(record.markaz, currentEvent);
  const namaazLabel = formatVenueLabel(record.namaazVenue, currentEvent);

  return (
    <Box sx={{ py: 0.25, pr: 0.5 }}>
      <Typography variant="body2" fontWeight={600} lineHeight={1.35}>
        {record.formNo ?? "—"}
      </Typography>
      {isAshara ? (
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          lineHeight={1.45}
          sx={{ wordBreak: "break-word" }}
        >
          {jamanLabel || "—"}
        </Typography>
      ) : (
        <>
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            lineHeight={1.45}
            sx={{ wordBreak: "break-word" }}
          >
            Jaman: {jamanLabel || "—"}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            lineHeight={1.45}
            sx={{ wordBreak: "break-word" }}
          >
            Namaaz: {namaazLabel || "—"}
          </Typography>
        </>
      )}
    </Box>
  );
};

const renderNiyaazHofCell = (record: RaRecord) => (
  <Box sx={{ py: 0.25, pr: 0.5 }}>
    <Typography variant="body2" fontWeight={600} lineHeight={1.35} sx={{ wordBreak: "break-word" }}>
      {record.HOFName || "—"}
    </Typography>
    <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.45}>
      ITS {record.HOFId || "—"}
    </Typography>
    {record.HOFPhone ? (
      <Typography variant="caption" color="text.secondary" component="div" lineHeight={1.45}>
        <PhoneLink phone={String(record.HOFPhone)} stopPropagation />
      </Typography>
    ) : null}
  </Box>
);

const niyaazDatagridSx = {
  minWidth: 1520,
  "& .RaDatagrid-table": {
    tableLayout: "auto",
  },
  [`& .${HOF_COLUMN_CLASS}`]: {
    minWidth: 240,
    width: "18%",
    maxWidth: 320,
    whiteSpace: "normal",
    verticalAlign: "top",
  },
  [`& .${FORM_VENUE_COLUMN_CLASS}`]: {
    minWidth: 140,
    width: "12%",
    maxWidth: 200,
    whiteSpace: "normal",
    verticalAlign: "top",
  },
} as const;

const niyaazYesNoField = (source: string, label: string) => (
  <FunctionField
    key={source}
    label={label}
    source={source}
    render={(record: RaRecord) => (record[source] ? "Yes" : "No")}
  />
);

const niyaazInrField = (source: string, label: string) => (
  <FunctionField
    key={source}
    label={label}
    source={source}
    textAlign="right"
    render={(record: RaRecord) => formatINR(record[source], { empty: "—" })}
  />
);

const niyaazNumberField = (source: string, label: string, emptyText?: string) => (
  <NumberField key={source} source={source} label={label} textAlign="right" emptyText={emptyText} />
);

export const NiyaazList = () => {
  const isSmall = useMediaQuery((theme) => theme.breakpoints.down("sm"), { noSsr: true });
  const [currentEvent] = useStore<CurrentEvent | null>("currentEvent", null);
  const isAshara = isAsharaEvent(currentEvent ?? undefined);

  const niyaazFilters = buildNiyaazListFilters(isAshara, currentEvent);
  const { permissions } = usePermissions();

  const ListActions = () => (
    <TopToolbar sx={{ justifyContent: "start" }}>
      <FilterButton />
      <SelectColumnsButton />
      {hasPermission(permissions, "niyaaz.create") && <CreateButton />}
      {hasPermission(permissions, "niyaaz.export") && <ExportButton />}
    </TopToolbar>
  );

  const exporter: Exporter = (niyaaz) => {
    const niyaazColumns = [
      { header: "Form No", field: "formNo", width: 15 },
      ...(isAshara
        ? [{ header: "Venue", field: "markaz", width: 15 }]
        : [
            { header: "Markaz", field: "markaz", width: 15 },
            { header: "Namaaz Venue", field: "namaazVenue", width: 15 },
          ]),
      { header: "HOF ID", field: "HOFId", width: 15 },
      { header: "HOF Name", field: "HOFName", width: 30 },
      { header: "Mobile", field: "HOFPhone", width: 15 },
      {
        header: "Members",
        field: (rec: RaRecord) => countNiyaazMembers(rec, isAshara),
        width: 10,
      },
      {
        header: isAshara ? "Niyaaz Amount" : "Takhmeen",
        field: "takhmeenAmount",
        width: 15,
      },
      { header: "Zabihat", field: "zabihat", width: 10 },
      { header: "Chairs", field: "chairs", width: 10 },
      ...(isAshara
        ? [
            { header: "Mehmaans", field: "mehmaanCount", width: 10 },
            {
              header: "Relay",
              field: (rec: RaRecord) => (rec.relayRequired ? "Yes" : "No"),
              width: 10,
            },
          ]
        : [{ header: "Iftaari", field: "iftaari", width: 12 }]),
      { header: "Pirsa", field: "pirsa", width: 10 },
      {
        header: "Send Reminders",
        field: (rec: RaRecord) => (rec.sendReminders ? "Yes" : "No"),
        width: 15,
      },
      {
        header: "Late Payment",
        field: (rec: RaRecord) => (rec.latePayment ? "Yes" : "No"),
        width: 12,
      },
      { header: "Total Payable", field: "totalPayable", width: 15 },
      { header: "Paid Amount", field: "paidAmount", width: 15 },
      { header: "Balance", field: "balance", width: 15 },
      {
        header: "Submitter",
        field: (rec: RaRecord) =>
          (rec?.admin as { name?: string } | undefined)?.name || rec.submitter || "",
        width: 20,
      },
    ];

    exportToExcel(niyaazColumns, niyaaz, {
      filenamePrefix: "niyaaz",
      sheetName: "Niyaaz",
    });
  };

  return (
    <List
      pagination={<Pagination rowsPerPageOptions={[5, 10, 25, 50]} />}
      filters={niyaazFilters}
      actions={<ListActions />}
      sort={{ field: "updatedAt", order: "DESC" }}
      exporter={hasPermission(permissions, "niyaaz.export") ? exporter : undefined}
    >
      {isSmall ? (
        <SimpleList
          primaryText={(record) => renderNiyaazFormVenueCell(record, currentEvent, isAshara)}
          secondaryText={(record) => (
            <>
              {String(record.HOFName)} · {String(record.HOFId)} ·{" "}
              {countNiyaazMembers(record, isAshara)} members
              <br />
              {formatINR(record.totalPayable)} · Paid {formatINR(record.paidAmount)} · Bal{" "}
              {formatINR(record.balance)}
              {record.latePayment ? " · Late payment" : ""}
              {record.sendReminders !== false ? " · Reminders on" : " · Reminders off"}
            </>
          )}
          linkType="show"
          rowSx={() => ({ borderBottom: 1, borderBottomColor: "divider" })}
        />
      ) : (
        <Box sx={{ overflowX: "auto", width: "100%" }}>
          <Datagrid
            preferenceKey="niyaaz.datagrid.v5"
            rowClick="show"
            bulkActionButtons={false}
            sx={niyaazDatagridSx}
          >
            <WrapperField source="EDIT" label="Edit">
              <EditButton />
            </WrapperField>
            <FunctionField
              label={isAshara ? "Form / Venue" : "Form / Venues"}
              source="formNo"
              sortBy="formNo"
              render={(record: RaRecord) =>
                renderNiyaazFormVenueCell(record, currentEvent, isAshara)
              }
              headerClassName={FORM_VENUE_COLUMN_CLASS}
              cellClassName={FORM_VENUE_COLUMN_CLASS}
            />
            <FunctionField
              label="HOF"
              source="HOFName"
              render={renderNiyaazHofCell}
              headerClassName={HOF_COLUMN_CLASS}
              cellClassName={HOF_COLUMN_CLASS}
            />
            <FunctionField
              label="Members"
              source="familyMembers"
              textAlign="right"
              render={(record: RaRecord) => countNiyaazMembers(record, isAshara)}
            />
            {niyaazInrField("takhmeenAmount", isAshara ? "Niyaaz Amount" : "Takhmeen")}
            {niyaazNumberField("zabihat", "Zabihat")}
            {niyaazNumberField("chairs", "Chairs")}
            {isAshara ? niyaazNumberField("mehmaanCount", "Mehmaans") : null}
            {isAshara ? niyaazYesNoField("relayRequired", "Relay") : null}
            {!isAshara ? niyaazInrField("iftaari", "Iftaari") : null}
            {niyaazNumberField("pirsa", "Pirsa", "—")}
            {niyaazInrField("totalPayable", "Total Payable")}
            {niyaazInrField("paidAmount", "Paid Amount")}
            {niyaazInrField("balance", "Balance")}
            {niyaazYesNoField("latePayment", "Late Payment")}
            {niyaazYesNoField("sendReminders", "Send Reminders")}
            <FunctionField
              label="Submitter"
              source="submitter"
              render={(record) => <span>{record?.admin?.name || record.submitter || "—"}</span>}
            />
            <DisplayDateField source="updatedAt" label="Last Updated" showTime emptyText="—" />
            <FunctionField
              label="Download"
              source="downloadPasses"
              sortable={false}
              render={(record) => (
                <Button
                  onClick={() =>
                    downloadPasses({
                      ...record,
                      familyMembers: buildPassMembers(record),
                      event: { slug: currentEvent?.slug ?? "" },
                      isAshara,
                      markazLabel: formatVenueLabel(record.markaz, currentEvent),
                      namaazVenueLabel: formatVenueLabel(record.namaazVenue, currentEvent),
                    } as Partial<PassesProps>)
                  }
                >
                  <DownloadIcon />
                </Button>
              )}
              key="name"
            />
          </Datagrid>
        </Box>
      )}
    </List>
  );
};
